import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button-variants";
import { Quote, Sparkles, Users, Clock, Home, FileText, ArrowLeft, ArrowRight, Heart } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import serigneDossehImage from "@/assets/serigne-dosseh.jpg";
import cassandraDableImage from "@/assets/cassandra-dable.jpg";

const FoundersStory = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [carouselApi, setCarouselApi] = useState<any>(null);

  // Update current slide when carousel changes
  if (carouselApi) {
    carouselApi.on('select', () => {
      setCurrentSlide(carouselApi.selectedScrollSnap());
    });
  }
  
  const features = [
    {
      icon: Users,
      title: "Accompagnement humain 7/7",
      description: "Une équipe dédiée toujours disponible pour vous accompagner",
      gradient: "from-primary to-primary/80",
      shadowColor: "shadow-primary/20",
    },
    {
      icon: Clock,
      title: "Matching ultra-rapide",
      description: "Trouvez votre mission idéale en moins de 48h",
      gradient: "from-accent to-accent/80",
      shadowColor: "shadow-accent/20",
    },
    {
      icon: Home,
      title: "Aide logement & avances de frais",
      description: "Prise en charge complète de vos déplacements et hébergement",
      gradient: "from-secondary to-secondary/80",
      shadowColor: "shadow-secondary/20",
    },
    {
      icon: FileText,
      title: "Zéro paperasse : on gère tout",
      description: "Concentrez-vous sur votre métier, on s'occupe de l'administratif",
      gradient: "from-accent to-accent-light",
      shadowColor: "shadow-accent/20",
    },
  ];

  return (
    <section className="py-20 md:py-28 bg-gradient-to-br from-white via-muted/10 to-white relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-1/2 left-0 w-72 h-72 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-secondary/10 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute top-20 -right-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-white/80 backdrop-blur-sm rounded-full mb-6 border border-primary/10 shadow-lg">
            <Heart className="w-4 h-4 text-primary" />
            <span className="text-primary font-semibold text-sm tracking-wide">QUI SOMMES-NOUS ?</span>
          </div>
          <h2 className="font-poppins font-black text-3xl md:text-5xl bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-4">
            Notre histoire & nos valeurs
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Une approche unique qui place l'humain au cœur de chaque mission
          </p>
        </div>

        {/* Founders Story Card */}
        <div className="max-w-5xl mx-auto mb-20">
          <div className="group relative">
            {/* Glow effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-primary via-secondary to-accent rounded-3xl opacity-20 blur-2xl group-hover:opacity-30 transition-opacity duration-700"></div>
            
            {/* Card */}
            <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-white/50">
              <div className="grid md:grid-cols-2">
                {/* Founders Images */}
                <div className="relative bg-gradient-to-br from-primary via-primary/95 to-secondary p-8 md:p-12 lg:p-16 flex items-center justify-center overflow-hidden">
                  {/* Decorative elements */}
                  <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:30px_30px]"></div>
                  <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
                  <div className="absolute bottom-0 left-0 w-40 h-40 bg-secondary/20 rounded-full blur-3xl"></div>
                  
                  <div className="text-center relative z-10 w-full">
                    <div className="flex gap-4 md:gap-8 justify-center mb-6 md:mb-8">
                      <div className="relative group/avatar">
                        <div className="absolute -inset-1 bg-white/30 rounded-full blur-md group-hover/avatar:blur-lg transition-all"></div>
                        <div className="relative w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-white/40 shadow-xl hover:scale-110 transition-transform duration-300">
                          <img 
                            src={serigneDossehImage} 
                            alt="Serigne Dosseh, co-fondateur de Mind Medical"
                            className="w-full h-full object-cover"
                            width="128" height="128" loading="lazy" decoding="async"
                          />
                        </div>
                      </div>
                      <div className="relative group/avatar">
                        <div className="absolute -inset-1 bg-white/30 rounded-full blur-md group-hover/avatar:blur-lg transition-all"></div>
                        <div className="relative w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-white/40 shadow-xl hover:scale-110 transition-transform duration-300">
                          <img 
                            src={cassandraDableImage} 
                            alt="Cassandra Dablé, co-fondatrice de Mind Medical"
                            className="w-full h-full object-cover"
                            width="128" height="128" loading="lazy" decoding="async"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-4 md:px-6 py-3 md:py-4 border border-white/20 mx-auto max-w-xs">
                      <p className="text-white font-bold text-base md:text-lg mb-1">
                        Serigne Dosseh & Cassandra Dablé
                      </p>
                      <p className="text-white/80 text-sm flex items-center justify-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        Co-fondateurs
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quote and CTA */}
                <div className="p-6 md:p-10 lg:p-14 flex flex-col justify-center bg-gradient-to-br from-white to-muted/20">
                  <div className="relative mb-6">
                    <Quote className="w-12 h-12 md:w-16 md:h-16 text-primary/20 absolute -top-3 md:-top-4 -left-2 md:-left-4" />
                    <blockquote className="font-poppins text-base md:text-xl lg:text-2xl text-foreground font-semibold leading-relaxed italic relative z-10 pl-4 md:pl-6">
                      "Nous avons vu trop de médecins ballotés de mission en mission, sans info, sans soutien. Notre objectif est simple : que chaque praticien se sente respecté, informé et accompagné comme il le mérite."
                    </blockquote>
                  </div>
                  <p className="text-muted-foreground leading-relaxed mb-8 text-sm md:text-base lg:text-lg">
                    Avec plus de 10 ans d'expérience dans le secteur médical, nous
                    avons bâti Mind Medical sur des valeurs simples : <span className="font-semibold text-primary">Bienveillance</span>, 
                    <span className="font-semibold text-secondary"> Transparence</span>, et <span className="font-semibold text-accent">Engagement humain</span>.
                  </p>
                  <Button 
                    variant="default" 
                    size="xl" 
                    className="bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group/btn"
                    asChild
                  >
                    <Link to="/about" className="relative">
                      <span className="relative z-10">Découvrir notre histoire</span>
                      <svg className="w-5 h-5 ml-2 group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pourquoi nous choisir - Features Grid */}
        <div className="mt-20">
          <h3 className="font-poppins font-bold text-2xl md:text-3xl text-center mb-12 text-foreground">
            Pourquoi nous choisir ?
          </h3>
          
          {/* Mobile Carousel */}
          <div className="sm:hidden">
            <Carousel 
              className="w-full max-w-sm mx-auto"
              setApi={setCarouselApi}
              opts={{
                align: "center",
                loop: true,
              }}
            >
              <CarouselContent>
                {features.map((feature, index) => (
                  <CarouselItem key={index}>
                    <div className="p-1">
                      <div className="group relative">
                        {/* Glow effect on hover */}
                        <div className={`absolute -inset-0.5 bg-gradient-to-r ${feature.gradient} rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500`}></div>
                        
                        {/* Card */}
                        <div className="relative bg-white/80 backdrop-blur-sm border border-border/50 rounded-2xl p-8 text-center hover:border-primary/30 hover:shadow-2xl transition-all duration-500 h-full flex flex-col">
                          <div className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 ${feature.shadowColor} shadow-lg`}>
                            <feature.icon className="w-10 h-10 text-white" strokeWidth={2.5} />
                          </div>
                          <h4 className="font-poppins font-bold text-xl mb-3 text-foreground group-hover:text-primary transition-colors">
                            {feature.title}
                          </h4>
                          <p className="text-muted-foreground text-sm leading-relaxed flex-1">
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              
              {/* Navigation Arrows - More visible on mobile */}
              <CarouselPrevious className="left-2 h-10 w-10 bg-white/90 backdrop-blur-sm text-primary hover:bg-primary hover:text-white shadow-lg border border-primary/20" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </CarouselPrevious>
              <CarouselNext className="right-2 h-10 w-10 bg-white/90 backdrop-blur-sm text-primary hover:bg-primary hover:text-white shadow-lg border border-primary/20" size="icon">
                <ArrowRight className="h-5 w-5" />
              </CarouselNext>
            </Carousel>
            
            {/* Dots Indicators */}
            <div className="flex justify-center gap-3 mt-6">
              {features.map((_, index) => (
                <button
                  key={index}
                  onClick={() => carouselApi?.scrollTo(index)}
                  className={`p-2 rounded-full transition-all duration-300 ${
                    currentSlide === index 
                      ? 'bg-primary' 
                      : 'bg-transparent'
                  }`}
                  aria-label={`Aller à la slide ${index + 1}`}
                >
                  <div className={`h-2 w-2 rounded-full transition-all duration-300 ${
                    currentSlide === index 
                      ? 'bg-white scale-125' 
                      : 'bg-muted-foreground/30'
                  }`} />
                </button>
              ))}
            </div>
          </div>

          {/* Desktop Grid */}
          <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group relative"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Glow effect on hover */}
                <div className={`absolute -inset-0.5 bg-gradient-to-r ${feature.gradient} rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500`}></div>
                
                {/* Card */}
                <div className="relative bg-white/80 backdrop-blur-sm border border-border/50 rounded-2xl p-8 text-center hover:border-primary/30 hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] h-full flex flex-col">
                  <div className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 ${feature.shadowColor} shadow-lg`}>
                    <feature.icon className="w-10 h-10 text-white" strokeWidth={2.5} />
                  </div>
                  <h4 className="font-poppins font-bold text-xl mb-3 text-foreground group-hover:text-primary transition-colors">
                    {feature.title}
                  </h4>
                  <p className="text-muted-foreground text-sm leading-relaxed flex-1">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FoundersStory;